import axios from 'axios';
import { API_URL } from '@/config.js';
import store from '@/store'; // Import the store instance
import { resolveInitialLocale } from '@/locales/tenantLocale';

const axiosInstance = axios.create({
  baseURL: API_URL,
  timeout: 120000, // 120 seconds (2 minutes) - for long-running article generation requests
  // Other default configurations
});

axiosInstance.interceptors.request.use(
  (config) => {
    const urlParams = new URLSearchParams(window.location.search);

    let chatbot_name = urlParams.get('chatbot_name');

    // If chatbot_name not in URL, detect from domain
    if (!chatbot_name) {
      const hostname = window.location.hostname;
      if (hostname.includes('meetscribe')) {
        chatbot_name = 'meetscribe';
      } else if (hostname.includes('clipcutbr')) {
        // IMPORTANT: check clipcutbr BEFORE clipcut — 'clipcut' is a substring of 'clipcutbr',
        // so app.clipcutbr.com would otherwise be misidentified as the RU clipcut tenant.
        chatbot_name = 'clipcutbr';
      } else if (hostname.includes('clipcut')) {
        chatbot_name = 'clipcut';
      } else if (hostname.includes('narezkavideo')) {
        chatbot_name = 'narezkavideo';
      } else if (hostname.includes('ca.harikatha') || hostname.includes('chaitanyaacademy')) {
        // IMPORTANT: Check ca.harikatha BEFORE harikatha to avoid false match
        chatbot_name = 'chaitanyaacademy';
      } else if (hostname.includes('harikatha')) {
        chatbot_name = 'harikatha';
      } else if (hostname.includes('edugrowth')) {
        chatbot_name = 'edugrowth';
      } else if (hostname.includes('zvonalitik')) {
        chatbot_name = 'zvonalitik';
      } else if (hostname.includes('vid2text') || hostname.includes('video2text')) {
        chatbot_name = 'vid2text';
      } else if (hostname.includes('youtest')) {
        chatbot_name = 'vid2text'; // youtest.ru uses vid2text bot
      } else if (hostname === 'localhost' || hostname === '127.0.0.1') {
        chatbot_name = 'zvonalitik'; // localhost defaults to zvonalitik for testing
      }
    }

    const authToken = localStorage.getItem('authToken');
    config.headers['Authorization'] = `Bearer ${authToken}`;
    //const urlParams = new URLSearchParams(window.location.search);
    const hash = store.getters['hash/getHash']; // Access the hash value from the hashModule using store getters
    if (hash) {
      config.params = {
        ...config.params,
        hash: hash,
      };
    }
    // Send chatbot_name for all requests when user has authToken
    // Only skip it for get_config_data without authToken (initial Telegram auth)
    const isConfigRequest = config.params?.action === 'get_config_data';
    const hasAuthToken = authToken && authToken !== 'null' && authToken !== 'undefined';
    const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';

    // Send chatbot_name if:
    // - Not a config request, OR
    // - Config request but user is already logged in (has authToken)
    // - OR we're on localhost (always send for testing)
    if (chatbot_name && (!isConfigRequest || hasAuthToken || isLocalhost)) {
      config.params = {
        ...config.params,
        chatbot_name: chatbot_name,
      };
    }

    // Add user's locale/language preference for backend localization.
    // Domain-aware: на BR-тенанте дефолт PT (а не хардкод 'ru'); сохранённая локаль
    // другого тенанта не протекает — resolveInitialLocale валидирует по домену.
    const userLocale = resolveInitialLocale();
    config.params = {
      ...config.params,
      lang: userLocale,
    };

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Helper function to handle logout on auth failure
const handleAuthFailure = () => {
  const hadToken = localStorage.getItem('authToken');

  // 2026-09-02: состояние сбрасываем ВСЕГДА, даже когда токена не было.
  // Раньше ветка «нет токена» выходила молча — и кабинет оставался полностью
  // интерактивным для сессии, которая не может записать ни байта: сервер видел
  // user_id=0 и отвечал 401 на КАЖДОЕ действие. 02.09 юзер так правил настройки
  // и жал «Сохранить», а в ответ получал только «попробуйте ещё раз».
  store.commit('user/setAuthentication', false);
  store.commit('user/setUser', null);

  // Перезагружаться, когда токена и не было, бессмысленно — она ничего не изменит
  // и зациклится. Состояние выше уже приведено в порядок, UI покажет вход.
  if (!hadToken) {
    return;
  }

  console.warn('Auth failure - logging out user');

  // Remove invalid token from localStorage
  localStorage.removeItem('authToken');

  // Reload page once to show login state (no redirect loop because token is now gone)
  window.location.reload();
};

axiosInstance.interceptors.response.use(
  (response) => {
    // Check for auth errors in response body (some endpoints return 200 with error)
    const data = response.data;

    // Skip auth check for public endpoints
    const url = response.config?.url || '';
    const isPublicEndpoint = url.includes('get_config_data') ||
                              url.includes('action=login') ||
                              url.includes('action=get_article');

    if (!isPublicEndpoint) {
      if (data && data.error === 'Auth required') {
        handleAuthFailure();
        return Promise.reject(new Error('Auth required'));
      }
      if (data && data.error === 'Invalid or expired token') {
        handleAuthFailure();
        return Promise.reject(new Error('Invalid or expired token'));
      }
    }
    return response;
  },
  (error) => {
    if (error.response && error.response.status === 401) {
      handleAuthFailure();
    }
    return Promise.reject(error);
  }
);

export default axiosInstance;
